import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Volume2, Loader2, Play, Pause, Square, FileText } from 'lucide-react';
import { useI18n } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

export default function ReadAloud({ screen, context }) {
  const { t, lang, isRTL } = useI18n();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rate, setRate] = useState(1);
  const utterRef = useRef(null);

  const stop = useCallback(() => {
    if (ttsSupported) window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const speak = useCallback((content, r = rate) => {
    if (!ttsSupported || !content) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(content);
    u.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
    u.rate = r;
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang?.toLowerCase().startsWith(lang === 'ar' ? 'ar' : 'en'));
    if (match) u.voice = match;
    u.onstart = () => { setSpeaking(true); setPaused(false); };
    u.onend = () => { setSpeaking(false); setPaused(false); };
    u.onerror = () => { setSpeaking(false); setPaused(false); };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, [lang, rate]);

  const generate = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setText('');
    stop();
    try {
      const res = await axios.post(`${API}/ai/summary`, { screen, lang, context }, { withCredentials: true });
      const summary = res.data.summary || '';
      setText(summary);
      speak(summary);
    } catch (e) {
      setText(lang === 'ar' ? 'تعذّر إنشاء الملخص. حاول مجدداً.' : 'Could not generate the summary. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [screen, lang, context, speak, stop]);

  const togglePause = () => {
    if (!ttsSupported) return;
    if (paused) { window.speechSynthesis.resume(); setPaused(false); }
    else { window.speechSynthesis.pause(); setPaused(true); }
  };

  const changeRate = (r) => {
    setRate(r);
    if (speaking && text) speak(text, r);
  };

  return (
    <div className="relative">
      <button onClick={generate} disabled={loading}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
        {t('listen')}
      </button>

      {open && (
        <div className={`absolute z-40 mt-2 w-80 ${isRTL ? 'start-0' : 'end-0'} bg-white rounded-2xl border border-slate-200 shadow-xl p-4 animate-fade-up`}>
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold text-slate-900 text-sm flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-indigo-600" /> {t('aiSummary')}
            </h4>
            <button onClick={() => { stop(); setOpen(false); }} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
          </div>

          {!ttsSupported && (
            <p className="mt-2 text-xs text-amber-600">{t('ttsUnsupported')}</p>
          )}

          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('generating')}
            </div>
          ) : (
            <>
              {ttsSupported && (
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={togglePause} disabled={!speaking}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-50">
                    {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                    {paused ? t('resume') : t('pause')}
                  </button>
                  <button onClick={stop} disabled={!speaking}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-50">
                    <Square className="w-3.5 h-3.5" /> {t('stop')}
                  </button>
                  {!speaking && text && (
                    <button onClick={() => speak(text)}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100 transition-colors">
                      <Play className="w-3.5 h-3.5" /> {t('play')}
                    </button>
                  )}
                </div>
              )}

              {ttsSupported && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>{t('speed')}</span><span>{rate.toFixed(2)}×</span>
                  </div>
                  <input type="range" min="0.5" max="1.75" step="0.25" value={rate}
                    onChange={(e) => changeRate(parseFloat(e.target.value))}
                    className="w-full accent-indigo-600 cursor-pointer" />
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-1">
                  <FileText className="w-3.5 h-3.5" /> {t('transcript')}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed max-h-48 overflow-auto">{text}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
